import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { stringify } from 'qs';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';

const IS_LT_MD = isLessThanBreakpoint('md');

function ShelterCityCard({ city }) {
  function handleClick() {
    sendAnalyticsEvent(ANALYTICS_EVENTS.SHELTER_SEARCHED, {
      view: ANALYTICS_VIEWS.HOME,
      view_feature: ANALYTICS_VIEW_FEATURES.CITY_CARDS
    });
  }

  return (
    <a
      href={`/shelters#locate=${stringify({
        query: { location: city.name }
      })}`}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="ShelterLocationCard br4 ba b--gray-200 bg-white flex flex-column gap-32 md:gap-40 lg:gap-40 ph8 pv8 ph10-md pv10-md">
        <div className="transition grow overflow-hidden">
          <SmartImage
            lazy
            Tag="div"
            className="bg-center contain w-100"
            src={city.picture_url}
            height="272px"
            loadingClass="bg-white"
            crop
          />
        </div>
        <p className="font-serif font-24 md:font-20 fw-medium lh-title tc">
          {city.name}
        </p>
      </div>
    </a>
  );
}

ShelterCityCard.propTypes = {
  city: PropTypes.shape({
    name: PropTypes.string,
    picture_url: PropTypes.string
  }).isRequired
};

export default function SheltersSection() {
  const splideRef = useRef(null);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [activeIndex, setActiveIndex] = useState(0);
  const { data: shelterCities } = useSelector(
    ({ server }) => server.shelterCities
  );

  const sheltersCount = shelterCities.length;

  const isNextButtonDisabled =
    activeIndex >= shelterCities.length - cardsPerPage;
  const isPrevButtonDisabled = activeIndex <= 0;

  function handleActiveSlide(slide) {
    setActiveIndex(slide.index);
  }

  function handleResize(splide) {
    setCardsPerPage(splide.options.perPage);
  }

  return (
    <section className="SheltersSection bg-sand section-vertical-padding">
      <div className="container-xl section-fade">
        <div className="flex justify-center tc-ns">
          <h3 className="font-28 md:font-40 lh-title mb10 mb12-md w-80-md w-50-lg ph6-lg">
            Explore trusted shelters and rescues near you
          </h3>
        </div>
        <div className="ShelterCitiesCarousel">
          <Splide
            ref={splideRef}
            className="overflow-hidden-ns"
            onActive={handleActiveSlide}
            onMounted={handleResize}
            onResized={handleResize}
            options={{
              arrows: false,
              drag: sheltersCount > 0,
              dragMinThreshold: 20,
              easing: 'cubic-bezier(0.5, 0, 0.5, 1)',
              flickMaxPages: cardsPerPage,
              flickPower: 300,
              gap: IS_LT_MD ? 12 : 24,
              keyboard: 'focused',
              pagination: false,
              perPage: cardsPerPage,
              speed: IS_LT_MD ? 350 : 700,
              snap: true,
              start: activeIndex,
              breakpoints: {
                1200: { perPage: 3 },
                960: { perPage: 2 },
                540: { perPage: 1 }
              }
            }}
          >
            {shelterCities.map((city, idx) => (
              <SplideSlide key={`${city.name}-${idx}`}>
                <ShelterCityCard city={city} />
              </SplideSlide>
            ))}
          </Splide>
        </div>
        <div className="mt10 mt12-md relative w-100" style={{ height: 2 }}>
          <span className="absolute bg-stone-700 db o-30 h-100 w-100" />
          <span
            className="absolute bg-stone-700 db h-100 transition"
            style={{
              width: `${(100 / sheltersCount) * (activeIndex + cardsPerPage)}%`
            }}
          />
        </div>
        <div className="dn db-ns mt6 secondary">
          <button
            type="button"
            className={classnames('bg-white br-100 mr4', {
              'o-60': isPrevButtonDisabled
            })}
            disabled={isPrevButtonDisabled}
            onClick={() => splideRef.current.go('<')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-left-md" />
            </div>
          </button>
          <button
            type="button"
            className={classnames('bg-white br-100', {
              'o-60': isNextButtonDisabled
            })}
            disabled={isNextButtonDisabled}
            onClick={() => splideRef.current.go('>')}
          >
            <div className="flex items-center justify-center ph3 pv3">
              <Icon name="fetch-arrow-right-md" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
}
